/** Stored channel/publication metadata only, never channel reachability or transcript access. */
import { z } from "zod";
import type { DataverseApi } from "../api/dataverse-api.js";
import { ApiError, ConfigurationError, ValidationError } from "../utils/errors.js";

// Use identical runtime/JSON-Schema syntax, including mixed case and absolute end.
const AGENT_ID_PATTERN =
  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$(?![\s\S])/;

const SELECT =
  "botid,publishedon,modifiedon,authenticationmode,applicationmanifestinformation,synchronizationstatus";
const MAX_MEMO_CHARS = 65536;
const MAX_MEMO_BYTES = 131072;
const MAX_KEYS = 64;
type FieldState = "available" | "absent" | "null" | "malformed" | "unsupported" | "over-limit";
interface Field<T> {
  value: T | null;
  state: FieldState;
}

export const getCopilotAgentChannelConfigurationInputSchema = z
  .object({
    agentId: z.string().regex(AGENT_ID_PATTERN),
  })
  .strict();

export const getCopilotAgentChannelConfigurationTool = {
  name: "get_copilot_agent_channel_configuration",
  description:
    "Read selected stored channel-registration and publication metadata for one exact Copilot Studio agent in the current Dataverse environment. Returns only known registration-presence labels, selected status/authentication labels and timestamps, with explicit absent, null, malformed, unsupported or over-limit metadata. One fixed-select row read; parser/output bounds do not change the existing Dataverse read transport. No nested manifests, endpoint URLs, application IDs, credentials, instructions, components or conversations are returned. Registration presence does not prove a channel is enabled, installed, published, reachable or authorized. No channel, authentication, permission or publication changes are made.",
  inputSchema: {
    type: "object" as const,
    properties: {
      agentId: {
        type: "string",
        pattern: AGENT_ID_PATTERN.source,
        description: "Exact existing agent GUID in the current Dataverse environment.",
      },
    },
    required: ["agentId"],
    additionalProperties: false,
  },
  annotations: { readOnlyHint: true, destructiveHint: false, idempotentHint: true },
};

function record(value: unknown): Record<string, unknown> | undefined {
  return value !== null && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : undefined;
}

function missing(value: unknown): "absent" | "null" | undefined {
  return value === undefined ? "absent" : value === null ? "null" : undefined;
}

function unavailable<T>(state: FieldState): Field<T> {
  return { value: null, state };
}

function timestamp(value: unknown): Field<string> {
  const absent = missing(value);
  if (absent) return unavailable(absent);
  // Dataverse timestamps are UTC. Do not accept date-only/local-time strings,
  // Date.parse's calendar normalization, or arbitrary source text.
  if (typeof value !== "string" || value.length > 28) return unavailable("malformed");
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.\d{1,7})?Z$/.exec(value);
  if (!match) return unavailable("malformed");
  const [year, month, day, hour, minute, second] = match.slice(1).map(Number) as [
    number,
    number,
    number,
    number,
    number,
    number,
  ];
  const leap = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
  const days = [31, leap ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  if (
    year < 1 ||
    month < 1 ||
    month > 12 ||
    day < 1 ||
    day > days[month - 1]! ||
    hour > 23 ||
    minute > 59 ||
    second > 59 ||
    !Number.isFinite(Date.parse(value))
  )
    return unavailable("malformed");
  return { value, state: "available" };
}

function label(value: unknown, known: readonly string[]): Field<string> {
  const absent = missing(value);
  if (absent) return unavailable(absent);
  if (typeof value !== "string") return unavailable("malformed");
  return known.includes(value) ? { value, state: "available" } : unavailable("unsupported");
}

function authentication(value: unknown): Field<string> {
  const absent = missing(value);
  if (absent) return unavailable(absent);
  if (typeof value !== "number" || !Number.isInteger(value)) return unavailable("malformed");
  // Official bot.authenticationmode option labels, not guessed runtime access.
  const labels = [
    "Unspecified",
    "None",
    "Integrated",
    "Custom Azure Active Directory",
    "Generic OAuth2",
  ];
  const known = labels[value];
  return known === undefined ? unavailable("unsupported") : { value: known, state: "available" };
}

function memo(value: unknown): { state: FieldState; data?: Record<string, unknown> } {
  const absent = missing(value);
  if (absent) return { state: absent };
  // These Dataverse memo columns are strings, not arbitrary JavaScript graphs.
  if (typeof value !== "string") return { state: "malformed" };
  if (value.length > MAX_MEMO_CHARS || Buffer.byteLength(value, "utf8") > MAX_MEMO_BYTES)
    return { state: "over-limit" };
  try {
    const data = record(JSON.parse(value));
    if (!data) return { state: "malformed" };
    if (Object.keys(data).length > MAX_KEYS) return { state: "over-limit" };
    return { state: "available", data };
  } catch {
    return { state: "malformed" };
  }
}

function child(parent: Record<string, unknown> | undefined, key: string, parentState: FieldState) {
  if (!parent) return { state: parentState };
  const value = parent[key];
  const absent = missing(value);
  if (absent) return { state: absent };
  const data = record(value);
  if (!data) return { state: "malformed" as const };
  if (Object.keys(data).length > MAX_KEYS) return { state: "over-limit" as const };
  return { state: "available" as const, data };
}

function registrations(raw: unknown) {
  const parsed = memo(raw);
  const known = ["teams", "microsoft365"] as const;
  const entries = known.map((name) => {
    if (!parsed.data) return { name, present: null, state: parsed.state };
    if (!Object.hasOwn(parsed.data, name))
      return { name, present: false, state: "absent" as const };
    if (!record(parsed.data[name])) return { name, present: null, state: "malformed" as const };
    return { name, present: true, state: "available" as const };
  });
  return {
    metadataState: parsed.state,
    registrations: entries,
    // Unknown keys are counted, never echoed: they may contain private identifiers.
    unrecognizedKeyCount: parsed.data
      ? Object.keys(parsed.data).filter(
          (key) => key !== "$kind" && !known.some((name) => name === key),
        ).length
      : null,
    coverage:
      "Only the stored teams and microsoft365 registration markers are recognized; this is not an exhaustive list of configured channels.",
  };
}

function synchronization(raw: unknown) {
  const parsed = memo(raw);
  const operation = child(parsed.data, "lastFinishedPublishOperation", parsed.state);
  const current = child(parsed.data, "currentSynchronizationState", parsed.state);
  const published = child(parsed.data, "lastPublishedDetails", parsed.state);
  return {
    metadataState: parsed.state,
    lastPublishStatus: operation.data
      ? label(operation.data.status, ["Succeeded", "Failed"])
      : unavailable<string>(operation.state),
    lastPublishCompletedAt: operation.data
      ? timestamp(operation.data.operationEnd)
      : unavailable<string>(operation.state),
    synchronizationState: current.data
      ? label(current.data.state, ["Synchronized", "Synchronizing"])
      : unavailable<string>(current.state),
    lastPublishedAuthentication: published.data
      ? label(published.data.authenticationMode, ["None", "Integrated"])
      : unavailable<string>(published.state),
    interpretation:
      "These selected JSON labels follow the existing Studio-observed reader, not a documented exhaustive synchronization schema. Stored status is not a channel health or current runtime verdict.",
  };
}

export async function handleGetCopilotAgentChannelConfiguration(
  context: { api: Pick<DataverseApi, "getRow"> | null },
  input: unknown,
) {
  const validation = getCopilotAgentChannelConfigurationInputSchema.safeParse(input);
  if (!validation.success)
    throw new ValidationError(
      "An exact agent GUID is required; no other input fields are accepted.",
    );
  if (!context.api)
    throw new ConfigurationError(
      "Dataverse is unavailable in the current environment; no channel configuration was read.",
    );
  const agentId = validation.data.agentId.toLowerCase();
  let raw: unknown;
  try {
    raw = await context.api.getRow("bots", agentId, SELECT);
  } catch {
    // No upstream message, status body, token, URL or row-derived name is echoed.
    throw new ApiError(
      "Copilot channel configuration read failed; stored configuration is unavailable, not empty. No channel or publication change was made.",
      502,
    );
  }
  const row = record(raw);
  if (
    !row ||
    typeof row.botid !== "string" ||
    !AGENT_ID_PATTERN.test(row.botid) ||
    row.botid.toLowerCase() !== agentId
  )
    throw new ApiError(
      "Copilot channel configuration response identity is missing or mismatched; no configuration verdict returned.",
      502,
    );
  const channelConfiguration = {
    agentId,
    returnedAgentIdentityMatched: true,
    environmentBinding:
      "current Dataverse client context; environment identity is not independently present in the returned bot row",
    publishedOn: timestamp(row.publishedon),
    modifiedOn: timestamp(row.modifiedon),
    draftAuthentication: authentication(row.authenticationmode),
    ...registrations(row.applicationmanifestinformation),
    synchronization: synchronization(row.synchronizationstatus),
    verification: {
      channelEnabled: "not-verified",
      installation: "not-verified",
      reachability: "not-verified",
      effectiveAccess: "not-verified",
      runtime: "not-verified",
    },
    limits: { memoCharacters: MAX_MEMO_CHARS, memoUtf8Bytes: MAX_MEMO_BYTES, objectKeys: MAX_KEYS },
  };
  return {
    text: "Read stored channel-registration and publication metadata for the exact agent. Missing or unrecognized metadata is explicit; a missing publication date is not proof the agent was never published. Registration presence does not verify enabled channels, installation, reachability or user access. No manifests, endpoint URLs, credentials, components, instructions or conversations are returned. No tenant changes were made.",
    structured: { channelConfiguration },
  };
}
